import type { Market } from '@backmarket/http-api'
import { toBcp47Locale } from '@backmarket/utils/string/toBcp47Locale'
import type { RouteRecordRaw } from 'vue-router'

import { ROUTES } from '~/scopes/auth/routes'

import { CHECKOUT } from './routes-names'

export default ({ market }: { market: Market }): RouteRecordRaw[] => {
  const defaultLocale = toBcp47Locale(market.defaultLocale)

  return [
    {
      path: `/:locale(${defaultLocale})/f2`,
      name: CHECKOUT.SELF,
      redirect: { name: CHECKOUT.CART },
      component: () => import('./layouts/CheckoutLayout.vue'),
      meta: {
        disableClientSideNavigation: () => true,
        layout: false,
      },
      children: [
        {
          path: 'cart',
          name: CHECKOUT.CART,
          component: () => import('./pages/Cart/Cart.vue'),
          meta: {
            middleware: ['log-attempt'],
          },
        },
        {
          path: 'authentication',
          name: CHECKOUT.AUTHENTICATION,
          component: () => import('./pages/Authentication/Authentication.vue'),
          meta: {
            guest: {
              required: true,
              redirection: { name: CHECKOUT.ADDRESS_CONFIRMATION },
            },
            middleware: ['log-attempt'],
          },
        },
        {
          path: 'post-authentication',
          name: CHECKOUT.POST_AUTHENTICATION,
          component: () =>
            import('./pages/PostAuthentication/PostAuthentication.vue'),
          meta: {
            auth: {
              required: true,
              redirection: { name: CHECKOUT.AUTHENTICATION },
            },
            middleware: ['log-attempt'],
          },
        },
        {
          path: 'shipping/confirmation-address',
          name: CHECKOUT.ADDRESS_CONFIRMATION,
          component: () =>
            import('./pages/AddressConfirmation/AddressConfirmation.vue'),
          meta: {
            auth: {
              required: true,
              redirection: { name: ROUTES.AUTH.LOGIN },
            },
            middleware: [
              'log-attempt',
              'redirect-if-cart-empty',
              'redirect-if-missing-shipping',
              'redirect-if-missing-collection-point',
            ],
          },
        },
        {
          path: 'shipping/shipping-address',
          name: CHECKOUT.SHIPPING_ADDRESS,
          component: () =>
            import('./pages/ShippingAddress/ShippingAddress.vue'),
          meta: {
            auth: {
              required: true,
              redirection: { name: ROUTES.AUTH.LOGIN },
            },
            middleware: ['log-attempt', 'redirect-if-cart-empty'],
          },
        },
        {
          path: 'shipping/billing-address',
          name: CHECKOUT.BILLING_ADDRESS,
          component: () => import('./pages/BillingAddress/BillingAddress.vue'),
          meta: {
            auth: {
              required: true,
              redirection: { name: ROUTES.AUTH.LOGIN },
            },
            middleware: ['log-attempt', 'redirect-if-cart-empty'],
          },
        },
        {
          path: 'services/bouygues-telecom',
          redirect: { name: CHECKOUT.SERVICES.BOUYGUES.SIGN_UP },
          meta: {
            auth: {
              required: true,
              redirection: { name: ROUTES.AUTH.LOGIN },
            },
            middleware: ['check-bouygues-experiment'],
          },
          children: [
            {
              path: 'sign-up',
              name: CHECKOUT.SERVICES.BOUYGUES.SIGN_UP,
              component: () =>
                import('./pages/BouyguesSignUp/BouyguesSignUp.vue'),
            },
            {
              path: 'phoneline-setup',
              name: CHECKOUT.SERVICES.BOUYGUES.PHONELINE_SETUP,
              component: () =>
                import(
                  './pages/BouyguesPhonelineSetup/BouyguesPhonelineSetup.vue'
                ),
            },
            {
              path: 'not-eligible',
              name: CHECKOUT.SERVICES.BOUYGUES.NOT_ELIGIBLE,
              component: () =>
                import(
                  './pages/BouyguesPhonelineSetup/components/NotEligible/NotEligible.vue'
                ),
            },
          ],
        },
        {
          path: 'payment',
          name: CHECKOUT.PAYMENT,
          component: () => import('./pages/Payment/Payment.vue'),
          meta: {
            auth: {
              required: true,
              redirection: { name: ROUTES.AUTH.LOGIN },
            },
            middleware: [
              'log-attempt',
              'redirect-if-cart-empty',
              'redirect-if-missing-shipping',
              'redirect-if-missing-collection-point',
            ],
          },
        },
      ],
    },
    {
      path: `/:locale(${defaultLocale})/f2/payment-result/:paymentId?`,
      name: CHECKOUT.PAYMENT_RESULT,
      component: () => import('./pages/Result/Result.vue'),
      meta: {
        disableClientSideNavigation: () => true,
        auth: {
          required: true,
          redirection: { name: ROUTES.AUTH.LOGIN },
        },
        middleware: ['log-attempt'],
      },
    },
  ]
}
